import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../../i18n';
import type { CampaignObjectiveId } from '../../types';
import { funnelRowCount, KPI_WITH_HINT, kpiIdsFor } from '../../domain/performance';
import { Bone, TextBone } from '../CampaignSkeleton/CampaignSkeleton';

/*
 * Performance's blocks while their numbers load (VOX-318), one per block and
 * each in the block's own classes, so it lands where the block will and at its
 * size. What is already known is drawn for real — the KPI labels and the
 * funnel's column heads come from the objective — and only the figures are
 * bones. No status here: `CampaignPerformance` announces the load once.
 */

/** The progress card (`PerformanceProgress`): three figures, the track, its axis. */
export const ProgressSkeleton: React.FC = () => (
  <div className="campaign-performance__card" data-testid="performance-skeleton-progress">
    <TextBone width={180} size={16} lineHeight={24} />
    <div className="campaign-performance__figures">
      {[0, 1, 2].map((i) => (
        <div key={i} className="campaign-performance__figure">
          <TextBone width={110} size={12} lineHeight={20} />
          <TextBone width={88} size={30} lineHeight={38} />
          <TextBone width={130} size={12} lineHeight={20} />
        </div>
      ))}
    </div>
    <div className="campaign-performance__axis">
      <Bone height={32} />
      <div className="campaign-performance__axis-legend">
        <TextBone width={24} size={12} lineHeight={20} />
        <TextBone width={64} size={12} lineHeight={20} />
      </div>
    </div>
  </div>
);

/**
 * The KPI row: the objective's own cards with their real labels — the labels
 * don't depend on the numbers — and a bone where each value goes.
 */
export const KpiSkeleton: React.FC<{ objective: CampaignObjectiveId }> = ({ objective }) => {
  const { t } = useTranslation(NS);
  return (
    <div className="campaign-performance__kpis" data-testid="performance-skeleton-kpis">
      {kpiIdsFor(objective).map((id) => (
        <div key={id} className="campaign-performance__stat">
          <span className="campaign-performance__stat-label">{t(`performance.kpis.${id}`)}</span>
          <TextBone width="55%" size={24} lineHeight={30} />
          {KPI_WITH_HINT.has(id) && <TextBone width="70%" size={12} lineHeight={20} />}
        </div>
      ))}
    </div>
  );
};

/** The funnel card: its title and hint, its column heads, one row per rung. */
export const FunnelSkeleton: React.FC<{ objective: CampaignObjectiveId }> = ({ objective }) => {
  const { t } = useTranslation(NS);
  // Audience, contacted, each of the objective's stages, conversion.
  const rows = funnelRowCount(objective);
  return (
    <section
      className="campaign-performance__card campaign-performance__funnel-card"
      aria-label={t('performance.funnel.title')}
      data-testid="performance-skeleton-funnel"
    >
      <div className="campaign-performance__funnel-head">
        <span className="campaign-performance__title">{t('performance.funnel.title')}</span>
        <span className="campaign-performance__meta campaign-performance__funnel-hint">
          {t('performance.funnel.hint')}
        </span>
      </div>
      <div className="campaign-performance__funnel-scroll">
        <div className="campaign-performance__funnel">
          <div className="campaign-performance__funnel-row campaign-performance__funnel-row--head">
            <span className="campaign-performance__kicker">{t('performance.funnel.colStage')}</span>
            <span className="campaign-performance__kicker">{t('performance.funnel.colCount')}</span>
            <span className="campaign-performance__kicker campaign-performance__center">
              {t('performance.funnel.colStep')}
            </span>
            <span className="campaign-performance__kicker campaign-performance__center">
              {t('performance.funnel.colCum')}
            </span>
          </div>
          {Array.from({ length: rows }, (_, i) => (
            <div
              key={i}
              className="campaign-performance__funnel-row campaign-performance__funnel-row--stage"
            >
              <div className="campaign-performance__stage">
                <TextBone width="70%" />
                <TextBone width="50%" size={12} lineHeight={20} />
              </div>
              {/* Bars anchor right and shorten down the ladder, as the real ones. */}
              <div className="campaign-performance__lane">
                <Bone width={`${100 - i * (60 / rows)}%`} height="100%" />
              </div>
              <span className="campaign-performance__pct">
                {i > 0 && <Bone width={44} height={22} />}
              </span>
              <span className="campaign-performance__pct">
                <Bone width={44} height={22} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/** Result details (`PerformanceLeak`): the donut card and the bars card side by side. */
export const LeakSkeleton: React.FC = () => {
  const { t } = useTranslation(NS);
  return (
    <section
      className="campaign-performance__leak"
      aria-label={t('performance.leak.title')}
      data-testid="performance-skeleton-leak"
    >
      <span className="campaign-performance__title">{t('performance.leak.title')}</span>
      <div className="campaign-performance__leak-row">
        <div className="campaign-performance__card campaign-performance__leak-donut">
          <TextBone width="70%" size={12} lineHeight={20} />
          <div className="campaign-performance__donut campaign-performance__donut--skeleton">
            <Bone width={164} height={164} shape="circle" />
          </div>
          <div className="campaign-performance__legend">
            {[0, 1, 2].map((i) => (
              <TextBone key={i} width="85%" size={12} lineHeight={20} />
            ))}
          </div>
        </div>
        <div className="campaign-performance__card campaign-performance__leak-bars">
          <TextBone width="45%" size={12} lineHeight={20} />
          <div className="campaign-skeleton__stack campaign-performance__bars-skeleton">
            {[90, 70, 52, 38, 24].map((w) => (
              <Bone key={w} width={`${w}%`} height={22} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/** The runway's calendar: its head with the month's arrows, five weeks, the legend. */
export const RunwaySkeleton: React.FC = () => (
  <section
    className="campaign-performance__card campaign-performance__runway"
    data-testid="performance-skeleton-runway"
  >
    <div className="campaign-performance__runway-head">
      <div className="campaign-performance__runway-title">
        <TextBone width={220} size={16} lineHeight={24} />
        <TextBone width={280} lineHeight={22} />
      </div>
      <div className="campaign-performance__runway-nav">
        <Bone width={32} height={32} />
        <TextBone width={132} />
        <Bone width={32} height={32} />
      </div>
    </div>
    <div className="campaign-performance__calendar-scroll">
      <div className="campaign-performance__grid">
        {Array.from({ length: 7 }, (_, i) => (
          <span key={`w-${i}`} className="campaign-performance__weekday">
            <TextBone size={12} lineHeight={20} />
          </span>
        ))}
        {Array.from({ length: 35 }, (_, i) => (
          <Bone key={i} height={56} />
        ))}
      </div>
    </div>
    <div className="campaign-performance__legend-row">
      {[0, 1, 2].map((i) => (
        <TextBone key={i} width={96} size={12} lineHeight={20} />
      ))}
    </div>
  </section>
);
